import { useState } from "react";
import {
  useDeleteRestaurantMutation,
  useFetchRestaurantsQuery,
  useToggleRestaurantIsActiveStatusMutation,
} from "../store";
import Pagination from "../components/common/Pagination";
import ToggleSwitch from "../components/common/ToggleSwitch";
import NavigationLinkBtn from "../components/common/NavigationLinkBtn";
import { FaEdit, FaPlus, FaTrash } from "react-icons/fa";
import TextField from "../components/common/TextField";
import useDebounce from "../hooks/useDebounce";
import { Link } from "react-router-dom";
import { Restaurant } from "../types";
import { showToast, ToastType } from "../utils/toast.utils";
import Modal from "../components/common/Modal";
import Button from "../components/common/Button";

const Restaurants = () => {
  const [page, setPage] = useState<number>(1);
  const [perPage, setPerPage] = useState<number>(20);
  const [displayName, setDisplayName] = useState<string>("");
  const debouncedDislayName = useDebounce(displayName, 400);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [restaurantToDelete, setRestaurantToDelete] =
    useState<Restaurant | null>(null);

  const [deleteRestaurant] = useDeleteRestaurantMutation();
  const [toggleIsActive] = useToggleRestaurantIsActiveStatusMutation();

  const confirmDelete = (restaurantToDelete: Restaurant) => {
    setRestaurantToDelete(restaurantToDelete);
    setShowConfirmModal(true);
  };

  const handleDeleteRestaurant = async () => {
    if (!restaurantToDelete) return;
    const result = await deleteRestaurant(restaurantToDelete._id);
    if (!result.error) {
      showToast("Restaurant supprimée", ToastType.SUCCESS);
    }
    setShowConfirmModal(false);
    setRestaurantToDelete(null);
  };

  const { data: restaurantsInfo, isLoading } = useFetchRestaurantsQuery({
    page,
    perPage,
    displayName: debouncedDislayName,
  });

  return (
    <div className="w-full mx-auto px-6 py-8">
      <div className="flex flex-col md:flex-row items-center justify-between mb-8 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Restaurants</h1>
          <p className="text-gray-500 mt-1">
            Gérez la liste de vos restaurants partenaires
          </p>
        </div>
        <div className="flex items-center gap-4 w-full md:w-auto">
          <div className="w-full md:w-64">
            <TextField
              id="search"
              value={displayName}
              onChange={(e: any) =>
                setDisplayName(e.target.value)
              }
              placeholder="Rechercher..."
            />
          </div>
          <Link
            to="/restaurants/create"
            className="flex items-center gap-2 bg-primary text-white px-4 py-2 rounded-lg font-medium hover:bg-opacity-90 transition-opacity"
          >
            <FaPlus /> Nouveau
          </Link>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm text-gray-600">
            <thead className="bg-gray-50 text-xs uppercase font-semibold text-gray-500 border-b border-gray-200">
              <tr>
                <th className="px-6 py-4">Restaurant</th>
                <th className="px-6 py-4">Ville / Adresse</th>
                <th className="px-6 py-4 text-center">Commission</th>
                <th className="px-6 py-4 text-center">Statut</th>
                <th className="px-6 py-4 text-center">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {isLoading ? (
                <tr>
                  <td colSpan={5} className="px-6 py-8 text-center">
                    Chargement...
                  </td>
                </tr>
              ) : restaurantsInfo?.data?.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-8 text-center text-gray-400">
                    Aucun restaurant trouvé.
                  </td>
                </tr>
              ) : (
                restaurantsInfo?.data?.map((row) => (
                  <tr
                    key={row._id}
                    className="hover:bg-gray-50 transition-colors duration-150"
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center text-gray-500 font-bold text-lg overflow-hidden shrink-0">
                           {/* Placeholder since logo isn't in list type yet */}
                           {row.displayName.charAt(0).toUpperCase()}
                        </div>
                        <span className="font-medium text-gray-900">
                          {row.displayName}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-gray-900 line-clamp-1">{row.address}</div>
                    </td>
                    <td className="px-6 py-4 text-center font-medium">
                      {row.payout}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex justify-center">
                        <ToggleSwitch
                          isOn={!row.isDisabled}
                          onToggle={() => {
                            toggleIsActive({
                              restaurantId: row._id,
                              isDisabled: !row.isDisabled,
                            });
                          }}
                        />
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-center gap-3">
                        <Link
                          to={row._id}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-full transition-colors"
                          title="Modifier"
                        >
                          <FaEdit size={18} />
                        </Link>
                        <button
                          onClick={() => confirmDelete(row)}
                          className="p-2 text-red-500 hover:bg-red-50 rounded-full transition-colors"
                          title="Supprimer"
                        >
                          <FaTrash size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <div className="border-t border-gray-200 px-6 py-4">
          <Pagination
            paginationInfo={{
              page,
              perPage,
              count: restaurantsInfo?.paginationInfo.count || 0,
            }}
            setPage={setPage}
            setPerPage={setPerPage}
          />
        </div>
      </div>

      {showConfirmModal && (
        <Modal
          onClose={() => setShowConfirmModal(false)}
          actionBar={
            <>
              <Button
                className="bg-gray-100 text-gray-700 hover:bg-gray-200 px-4 py-2 rounded-lg mr-2"
                onClick={() => setShowConfirmModal(false)}
              >
                Annuler
              </Button>
              <Button
                className="bg-red-600 text-white hover:bg-red-700 px-4 py-2 rounded-lg shadow-sm"
                onClick={handleDeleteRestaurant}
              >
                Supprimer
              </Button>
            </>
          }
        >
          <div className="text-center p-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Confirmer la suppression
            </h3>
            <p className="text-gray-500">
              Êtes-vous sûr de vouloir supprimer{" "}
              <span className="font-bold text-gray-800">
                {restaurantToDelete?.displayName}
              </span>{" "}
              ? Cette action est irréversible.
            </p>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default Restaurants;
